import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { invokeAsExtension } from '../../extensions/capabilities/enforce';
import { openDbManagerTab, setDbManagerTabs } from '../../store/tabsStore';
import { getDbQueryResult, setDbQueryResult } from '../../store/dbQueryResultCache';
import { useNotificationStore } from '../../store/notificationStore';
import { getForeignKeys } from '../../extensions/dbEngines';
import { buildFkFilterFragment } from '../dbGridSql';
import { EMPTY_COLUMN_WIDTHS } from '../dbGridModel';
import { hiddenSet, liveHidden, toggleColumn } from '../dbColumnVisibility';
import { formatSql } from '../dbSqlFormat';
import useDbRefreshShortcut from './useDbRefreshShortcut';
import { selectedRowIndexes } from './dbExportSelection';
import { engineOf } from './dbResultsTabHelpers';

/** Foreign keys, column widths and visibility, view state and refresh for the results grid. */
export function useDbGridView({
  tab,
  result,
  isQueryTab,
  sqlDraft,
  setSqlDraft,
  loading,
  pendingRows,
  pendingEdits,
  pendingDeletes,
  runTableQuery,
  selectedRows,
  selectedCells,
  selectionAnchorRef,
  cellAnchorRef,
  transposed,
  runningQueryIdRef,
  scrollTopRef,
  fetchRowCount,
  resetLoadMore,
}) {
  // Foreign keys declared on this table. A failure resolves to [] inside
  // getForeignKeys, so a server that won't report them costs the navigate
  // button and nothing else. The cancelled flag rather than a request-id guard:
  // this state never feeds a write path, so the only hazard is a late response
  // from a tab we have left.
  const [foreignKeys, setForeignKeys] = useState([]);
  useEffect(() => {
    setForeignKeys([]);
    if (!tab?.tableName || tab.needsReconnect) return undefined;
    let cancelled = false;
    getForeignKeys(engineOf(tab), tab.extensionId, tab.connId, tab.dbName, tab.tableName)
      .then((fks) => { if (!cancelled) setForeignKeys(fks); })
      .catch(() => { if (!cancelled) setForeignKeys([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab?.id, tab?.needsReconnect]);

  const fkByColumn = useMemo(() => {
    const map = new Map();
    for (const fk of foreignKeys) {
      // First declaration wins when a column belongs to several keys; the
      // button's tooltip names the constraint so the choice is visible. The
      // Value Panel's FK switcher is where picking between them belongs.
      fk.columns.forEach((col, i) => {
        if (!map.has(col)) map.set(col, { fk, memberIndex: i });
      });
    }
    return map;
  }, [foreignKeys]);

  /** Open the referenced table, narrowed to the row this cell points at. */
  const navigateFk = useCallback((rowIndex, colIndex) => {
    if (!result) return;
    const hit = fkByColumn.get(result.columns[colIndex]);
    if (!hit) return;
    const engine = engineOf(tab);
    // Effective values, so navigating from a cell you have edited but not yet
    // saved follows what is on screen rather than what is in the database.
    const values = hit.fk.columns.map((col) => {
      const idx = result.columns.indexOf(col);
      if (idx === -1) return null;
      const key = `${rowIndex}:${idx}`;
      return Object.prototype.hasOwnProperty.call(pendingEdits, key)
        ? pendingEdits[key]
        : result.rows[rowIndex]?.[idx];
    });
    const filterText = buildFkFilterFragment({ engine, fk: hit.fk, values });
    if (filterText == null) return; // a NULL member points at no row
    openDbManagerTab({
      extensionId: tab.extensionId,
      connId: tab.connId,
      engine,
      dbName: hit.fk.refDb || tab.dbName,
      tableName: hit.fk.refTable,
      label: hit.fk.refTable,
      filterText,
    });
  }, [result, fkByColumn, tab, pendingEdits]);

  const columnWidths = tab?.columnWidths || EMPTY_COLUMN_WIDTHS;
  const transposedWidths = tab?.transposedWidths || EMPTY_COLUMN_WIDTHS;

  // Hidden columns ride on the tab, so they survive a re-query, a workspace
  // switch, and a restart — the same lifetime as column widths.
  const hiddenColumnNames = tab?.hiddenColumns;
  const hiddenColumns = useMemo(() => hiddenSet(hiddenColumnNames), [hiddenColumnNames]);
  const hiddenLive = useMemo(
    () => liveHidden(result?.columns, hiddenColumnNames),
    [result, hiddenColumnNames]
  );

  /**
   * Indexes of the columns still drawn, ascending.
   *
   * Hiding stays index-transparent for everything that ADDRESSES a cell —
   * pending edits, the WHERE builder, export, FK navigation — which is what
   * dbColumnVisibility's module doc is protecting, and none of that changes
   * here. Clipboard actions are a different question: they follow what the
   * user can see. Copying has to not emit a value that is not on screen, and
   * pasting has to not stage an edit onto a column the user cannot look at
   * afterwards to check — that edit is invisible right up until Save writes it
   * to the server.
   */
  const visibleColIndexes = useMemo(() => {
    const cols = result?.columns;
    if (!Array.isArray(cols)) return [];
    const out = [];
    for (let i = 0; i < cols.length; i++) if (!hiddenColumns.has(cols[i])) out.push(i);
    return out;
  }, [result, hiddenColumns]);

  /**
   * The same list, but answering for whichever view is on screen.
   *
   * The transposed/record view turns every column into a ROW and is rendered
   * without the hidden set, so there a hidden column is still drawn. "Follow
   * what is drawn" therefore points the other way: filtering there would skip
   * a field the user can plainly see, and a shift-sweep across it would drop
   * a value out of the middle of the copy.
   */
  const drawnColIndexes = useMemo(() => {
    if (!transposed) return visibleColIndexes;
    const cols = result?.columns;
    return Array.isArray(cols) ? cols.map((_, i) => i) : [];
  }, [transposed, result, visibleColIndexes]);

  const setHiddenColumns = useCallback((next) => {
    if (!tab) return;
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, hiddenColumns: next } : t)));
  }, [tab]);

  /**
   * Reformat the editor's contents.
   *
   * Committed the same way typing is, so the workspace snapshot mirror sees it
   * — otherwise a format followed by a workspace switch would be lost.
   */
  const formatDraft = useCallback(() => {
    if (!tab) return;
    const next = formatSql(sqlDraft);
    if (!next || next === sqlDraft) return;
    setSqlDraft(next);
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, sql: next } : t)));
  }, [tab, sqlDraft, setSqlDraft]);

  /**
   * The rows a row-level action should act on.
   *
   * The selection when there is one, otherwise just the row that was
   * right-clicked — so "Copy rows" means what it looks like it means whether or
   * not anything is selected.
   */
  const rowsForAction = useCallback(
    (clickedRowIndex) => {
      const selected = selectedRowIndexes(selectedRows, selectedCells);
      if (selected.length > 0) return selected;
      return clickedRowIndex == null ? [] : [clickedRowIndex];
    },
    [selectedRows, selectedCells]
  );

  /** Those rows as a result-shaped object, pending edits applied. */
  const resultForRows = useCallback(
    (indexes) => {
      if (!result) return null;
      const rows = indexes
        .filter((i) => result.rows[i])
        .map((i) =>
          result.columns.map((_, j) => {
            const key = `${i}:${j}`;
            return Object.prototype.hasOwnProperty.call(pendingEdits, key)
              ? pendingEdits[key]
              : result.rows[i][j];
          })
        );
      return { ...result, rows };
    },
    [result, pendingEdits]
  );

  /**
   * Stop the running query.
   *
   * On MySQL and postgres this is a REAL cancel — the backend sends KILL QUERY
   * / pg_cancel_backend on a second connection and the statement dies on the
   * server. SQLite has no such side channel here, so it falls back to
   * abandoning the wait while the statement runs to completion.
   *
   * The wording follows that split rather than papering over it: telling
   * someone a runaway UPDATE was cancelled when it is still running would be a
   * lie with consequences, and so would refusing to say it was cancelled when
   * it actually was.
   */
  const stopRunningQuery = useCallback(() => {
    const id = runningQueryIdRef.current;
    if (!id || !tab) return;
    invokeAsExtension('db_stop_query', { extensionId: tab.extensionId, queryId: id }).catch(() => {
      // A stop that races the result has nothing to signal. Nothing to report.
    });
  }, [tab, runningQueryIdRef]);

  const hideColumn = useCallback((col) => {
    const next = toggleColumn(hiddenColumnNames, col, result?.columns);
    // null means the toggle was refused — hiding the last visible column
    // would leave a grid with no header to right-click to undo it.
    if (next) setHiddenColumns(next);
  }, [hiddenColumnNames, result, setHiddenColumns]);

  /**
   * Commit a column width. `col === null` seeds every column at once (the first
   * drag pins the auto-laid-out widths before switching to a fixed layout); a
   * null width clears one back to automatic.
   */
  const handleColumnResize = useCallback((col, width) => {
    if (!tab) return;
    setDbManagerTabs((prev) => prev.map((t) => {
      if (t.id !== tab.id) return t;
      if (col === null) return { ...t, columnWidths: width };
      const next = { ...(t.columnWidths || {}) };
      if (width == null) delete next[col];
      else next[col] = Math.round(width);
      return { ...t, columnWidths: next };
    }));
  }, [tab]);

  /** Same contract, for the record view's own widths. */
  const handleTransposedResize = useCallback((key, width) => {
    if (!tab) return;
    setDbManagerTabs((prev) => prev.map((t) => {
      if (t.id !== tab.id) return t;
      if (key === null) return { ...t, transposedWidths: width };
      const next = { ...(t.transposedWidths || {}) };
      if (width == null) delete next[key];
      else next[key] = Math.round(width);
      return { ...t, transposedWidths: next };
    }));
  }, [tab]);

  const pendingEditCount = Object.keys(pendingEdits).length;

  // Sorting/paging/filtering/importing swap out the addressable row set, and
  // pending edit keys are row-index-based — refuse all of them while changes
  // are unsaved.
  const pendingDeleteCount = pendingDeletes.size;
  // Staged deletes count as unsaved work like any other: sort/page/filter and
  // refresh all refuse while they exist, because every one of them replaces
  // the row set these row INDEXES address.
  const hasUnsavedGridChanges =
    pendingRows.length > 0 || pendingEditCount > 0 || pendingDeleteCount > 0;

  /**
   * Record where the grid is scrolled to.
   *
   * A plain ref write, so scrolling does not re-render. It is persisted when
   * the tab is left rather than on every event: the cache mirror only runs
   * when the RESULT changes, so relying on it would save a stale position for
   * the exact case this is for — scroll, then immediately switch tabs.
   */
  const rememberScrollTop = useCallback((top) => {
    scrollTopRef.current = top;
  }, [scrollTopRef]);

  /**
   * The rest of "where I was", alongside the scroll position.
   *
   * A ref for the same reason `scrollTopRef` is one: the cleanup below reads it
   * on the way out, and making the selection a dependency would re-run the
   * effect — and rewrite the cache — on every click in the grid.
   *
   * The selection is index-based, which is only sound because the arrival path
   * restores it exclusively when it also restored the very same cached rows.
   *
   * Written from an effect with no dependency array, NOT from the render body.
   * No deps means "after every commit", which is the semantics wanted — and
   * unlike a render-body assignment it cannot record a render that React went on
   * to throw away, or run twice under StrictMode. Same convention as every other
   * ref here: `scrollTopRef` is written from a handler, `cellAnchorRef` from a
   * setter, and none of them are written mid-render.
   */
  const viewStateRef = useRef(null);
  useEffect(() => {
    viewStateRef.current = {
      selectedRows,
      selectedCells,
      cellAnchor: cellAnchorRef.current,
      rowAnchor: selectionAnchorRef.current,
      transposed,
    };
  });

  // Persist the final position and view on the way out, for the tab being left.
  useEffect(() => {
    const leavingId = tab?.id;
    const isTable = !!tab?.tableName;
    return () => {
      if (!leavingId || !isTable) return;
      const entry = getDbQueryResult(leavingId);
      if (!entry?.table) return;
      setDbQueryResult(leavingId, {
        ...entry,
        table: { ...entry.table, scrollTop: scrollTopRef.current, view: viewStateRef.current },
      });
    };
  }, [tab?.id, tab?.tableName, scrollTopRef]);

  /**
   * Re-run the current query and row count.
   *
   * Refuses while edits are staged for the same reason the filter and sort do:
   * a fresh result set replaces the rows that row-index-keyed pending edits
   * address, so it would silently discard work.
   */
  const refreshRows = useCallback(() => {
    if (!tab || isQueryTab || hasUnsavedGridChanges || loading) return;
    resetLoadMore();
    runTableQuery(tab, tab.filterText || '');
    fetchRowCount(tab, tab.filterText || '');
  }, [tab, isQueryTab, hasUnsavedGridChanges, loading, resetLoadMore, runTableQuery, fetchRowCount]);
  // Ctrl+R / F5: this Refresh, never an app reload (dbManager/useDbRefreshShortcut.js).
  useDbRefreshShortcut({
    isQueryTab,
    blocked: hasUnsavedGridChanges,
    busy: loading,
    refresh: refreshRows,
    onBlocked: () => useNotificationStore.getState().warning('Save or discard pending changes first.', 'Refresh'),
  });

  return {
    fkByColumn,
    navigateFk,
    columnWidths,
    transposedWidths,
    hiddenColumnNames,
    hiddenColumns,
    hiddenLive,
    drawnColIndexes,
    setHiddenColumns,
    formatDraft,
    rowsForAction,
    resultForRows,
    stopRunningQuery,
    hideColumn,
    handleColumnResize,
    handleTransposedResize,
    pendingEditCount,
    pendingDeleteCount,
    hasUnsavedGridChanges,
    rememberScrollTop,
    viewStateRef,
    refreshRows,
  };
}
